import { useEffect, useRef } from 'react';

import { PieArcDatum } from 'd3';

import { usePrevious } from '../../../../../../../../utils/hooks';
import { BREATHING_ROOM } from '../constants';

export interface UseSlice<T extends object> {
  slice: PieArcDatum<T>;
  radius: number;
}

const useSlice = <T extends object>({ slice, radius }: UseSlice<T>) => {
  const sliceRef = useRef<SVGPathElement | null>(null);
  const oldSlice = usePrevious(slice);

  const labelRef = useRef<SVGTextElement | null>(null);

  // Compute an arc generator = a function that transforms arc coordinates in a svg path
  const arcPathGenerator = d3.arc();

  useEffect(() => {
    if (!sliceRef.current) return;

    d3.select(sliceRef.current)
      .transition()
      .duration(500)
      .attrTween('d', () => {
        // Interpolate the start and end angles for smooth animation
        const interpolateStart = d3.interpolate(oldSlice?.startAngle || 0, slice.startAngle);
        const interpolateEnd = d3.interpolate(oldSlice?.endAngle || 0, slice.endAngle);

        return (t) =>
          arcPathGenerator({
            innerRadius: 0,
            outerRadius: radius * BREATHING_ROOM,
            startAngle: interpolateStart(t),
            endAngle: interpolateEnd(t),
          }) || '';
      });
  }, [slice, radius]);

  useEffect(() => {
    if (!labelRef.current) return;

    const computeLabelPosition = () => {
      // Interpolate the start and end angles for smooth animation
      const interpolateStart = d3.interpolate(oldSlice?.startAngle || 0, slice.startAngle);
      const interpolateEnd = d3.interpolate(oldSlice?.endAngle || 0, slice.endAngle);

      return (t: number) =>
        arcPathGenerator.centroid({
          innerRadius: radius * BREATHING_ROOM,
          outerRadius: radius,
          startAngle: interpolateStart(t),
          endAngle: interpolateEnd(t),
        });
    };

    d3.select(labelRef.current)
      .transition()
      .duration(500)
      .attrTween('x', () => {
        return (t) => `${computeLabelPosition()(t)[0]}px`;
      })
      .attrTween('y', () => {
        return (t) => `${computeLabelPosition()(t)[1]}px`;
      });
  }, [slice, radius]);

  return { sliceRef, labelRef };
};

export default useSlice;
